// Marketplace administration: tabs for Categories, Brands, Manufacturers,
// Products pending approval, Featured, Reviews, Banners
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import MasterCrudPage from "@/components/MasterCrudPage";

function ProductsPending() {
  const [items, setItems] = useState([]);
  const load = async () => { const { data } = await api.get("/admin/marketplace/products", { params: { status: "PENDING" } }); setItems(data); };
  useEffect(() => { load(); }, []);
  const act = async (id, action) => {
    try { await api.post(`/admin/marketplace/products/${id}/moderate`, { action }); toast.success(`Product ${action.toLowerCase()}d`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="surface-card p-4 overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow><TableHead>Title</TableHead><TableHead>Vendor</TableHead><TableHead>Price</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Moderation</TableHead></TableRow>
        </TableHeader>
        <TableBody>
          {items.map((p) => (
            <TableRow key={p.id} data-testid={`pending-${p.id}`}>
              <TableCell className="font-semibold">{p.title}</TableCell>
              <TableCell className="text-xs">{p.tenant_id?.slice(0,8)}</TableCell>
              <TableCell>₹{(p.price || 0).toLocaleString()}</TableCell>
              <TableCell><Badge variant="outline">{p.moderation_status || "DRAFT"}</Badge></TableCell>
              <TableCell className="text-right space-x-2">
                <Button size="sm" onClick={() => act(p.id, "APPROVE")} data-testid={`approve-${p.id}`}>Approve</Button>
                <Button size="sm" variant="outline" onClick={() => act(p.id, "REJECT")} data-testid={`reject-${p.id}`}>Reject</Button>
              </TableCell>
            </TableRow>
          ))}
          {items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">No products awaiting moderation.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </div>
  );
}

function Reviews() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/marketplace/reviews").then(({ data }) => setItems(data)); }, []);
  const act = async (id, action) => {
    try { await api.post(`/admin/marketplace/reviews/${id}/moderate`, { action }); toast.success(`Review ${action.toLowerCase()}d`); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="surface-card p-4 overflow-x-auto">
      <Table>
        <TableHeader><TableRow><TableHead>Reviewer</TableHead><TableHead>Rating</TableHead><TableHead>Text</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
        <TableBody>
          {items.map((r) => (
            <TableRow key={r.id}>
              <TableCell className="text-sm">{r.reviewer_email}</TableCell><TableCell>{r.rating}/5</TableCell>
              <TableCell className="text-xs max-w-md truncate">{r.text}</TableCell><TableCell><Badge variant="outline">{r.status || "PENDING"}</Badge></TableCell>
              <TableCell className="text-right space-x-2">
                <Button size="sm" onClick={() => act(r.id, "APPROVE")}>Approve</Button>
                <Button size="sm" variant="outline" onClick={() => act(r.id, "REJECT")}>Reject</Button>
              </TableCell>
            </TableRow>
          ))}
          {items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">No reviews yet.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </div>
  );
}

export default function Marketplace() {
  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="admin-marketplace">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-black">Marketplace administration</h1>
        <p className="text-muted-foreground">Categories, brands, manufacturers, dealer listings, product moderation, banners and reviews.</p>
      </div>
      <Tabs defaultValue="categories">
        <TabsList>
          <TabsTrigger value="categories" data-testid="tab-categories">Categories</TabsTrigger>
          <TabsTrigger value="brands" data-testid="tab-brands">Brands</TabsTrigger>
          <TabsTrigger value="manufacturers" data-testid="tab-manufacturers">Manufacturers</TabsTrigger>
          <TabsTrigger value="pending" data-testid="tab-pending">Products pending</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="tab-reviews">Reviews</TabsTrigger>
          <TabsTrigger value="banners" data-testid="tab-banners">Banners</TabsTrigger>
        </TabsList>
        <TabsContent value="categories"><MasterCrudPage kind="categories" title="" description="" /></TabsContent>
        <TabsContent value="brands"><MasterCrudPage kind="brands" title="" description="" /></TabsContent>
        <TabsContent value="manufacturers"><MasterCrudPage kind="manufacturers" title="" description="" /></TabsContent>
        <TabsContent value="pending"><ProductsPending /></TabsContent>
        <TabsContent value="reviews"><Reviews /></TabsContent>
        <TabsContent value="banners"><MasterCrudPage kind="banners" title="" description="" extra={[{key: "image_url", label: "Image URL"}, {key: "link", label: "Link"}]} /></TabsContent>
      </Tabs>
    </div>
  );
}
